import { zodResolver } from '@hookform/resolvers/zod';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Edit, FolderOpen, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { fetchAllAssetCategories } from '../lib/assetCategories';
import Header from '../components/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { createBadgeColumn, createDateColumn, createLinkColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface AssetCategory {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

interface RoomType {
  id: string;
  key: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  categories?: AssetCategory[];
}

const roomTypeSchema = z.object({
  key: z.string().min(1, 'Key is required').max(50).regex(/^[a-z][a-z0-9_]*$/, 'Key must be lowercase with underscores only'),
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(1000).optional(),
  icon: z.string().max(50).optional(),
  sort_order: z.coerce.number().int().min(0),
  is_active: z.boolean(),
});

type RoomTypeForm = z.infer<typeof roomTypeSchema>;

export default function RoomTypes() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [allCategories, setAllCategories] = useState<AssetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RoomType | null>(null);

  // Category management dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType | null>(null);
  const [availableCategories, setAvailableCategories] = useState<AssetCategory[]>([]);
  const [assignedCategories, setAssignedCategories] = useState<AssetCategory[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [selectedAssigned, setSelectedAssigned] = useState<Set<string>>(new Set());
  const [savingCategories, setSavingCategories] = useState(false);

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roomTypeToDelete, setRoomTypeToDelete] = useState<RoomType | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const form = useForm<RoomTypeForm>({
    resolver: zodResolver(roomTypeSchema),
    defaultValues: {
      key: '',
      name: '',
      description: '',
      icon: '',
      sort_order: 0,
      is_active: true,
    },
  });

  useEffect(() => {
    checkAuth();
    loadRoomTypes();
    loadAllCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (!token || !savedUser) {
      navigate('/login');
      return;
    }

    try {
      setUser(JSON.parse(savedUser));
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
    }
  };

  const loadRoomTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/room-types?include_inactive=true', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setRoomTypes(data.success ? data.data : []);
      } else {
        console.error('Failed to fetch room types');
      }
    } catch (error) {
      console.error('Error loading room types:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllCategories = async () => {
    try {
      setAllCategories(await fetchAllAssetCategories(false));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const startCreate = () => {
    setEditing(null);
    form.reset({ key: '', name: '', description: '', icon: '', sort_order: 0, is_active: true });
    setDialogOpen(true);
  };

  const startEdit = (roomType: RoomType) => {
    setEditing(roomType);
    form.reset({
      key: roomType.key,
      name: roomType.name,
      description: roomType.description || '',
      icon: roomType.icon || '',
      sort_order: roomType.sort_order,
      is_active: roomType.is_active,
    });
    setDialogOpen(true);
  };

  const onSubmit = async (values: RoomTypeForm) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const url = editing ? `/api/room-types/${editing.id}` : '/api/room-types';
      const method = editing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await loadRoomTypes();
        setDialogOpen(false);
        form.reset();
      } else {
        setErrorMessage(data.message || 'Failed to save room type');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error saving room type:', error);
      setErrorMessage('An error occurred while saving');
      setErrorDialogOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (roomType: RoomType) => {
    setRoomTypeToDelete(roomType);
    setDeleteDialogOpen(true);
  };

  const deleteRoomType = async () => {
    if (!roomTypeToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/room-types/${roomTypeToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setDeleteDialogOpen(false);
        setRoomTypeToDelete(null);
        await loadRoomTypes();
      } else {
        setDeleteDialogOpen(false);
        setErrorMessage('Failed to delete room type');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error deleting room type:', error);
      setDeleteDialogOpen(false);
      setErrorMessage('An error occurred while deleting');
      setErrorDialogOpen(true);
    }
  };

  // Category management functions
  const openCategoryDialog = useCallback((roomType: RoomType) => {
    setSelectedRoomType(roomType);
    const assignedIds = new Set(roomType.categories?.map(c => c.id) || []);
    setAssignedCategories(roomType.categories || []);
    setAvailableCategories(allCategories.filter(c => !assignedIds.has(c.id)));
    setSelectedAvailable(new Set());
    setSelectedAssigned(new Set());
    setCategoryDialogOpen(true);
  }, [allCategories]);

  const moveToAssigned = () => {
    const toMove = availableCategories.filter(c => selectedAvailable.has(c.id));
    setAssignedCategories(prev => [...prev, ...toMove]);
    setAvailableCategories(prev => prev.filter(c => !selectedAvailable.has(c.id)));
    setSelectedAvailable(new Set());
  };

  const moveToAvailable = () => {
    const toMove = assignedCategories.filter(c => selectedAssigned.has(c.id));
    setAvailableCategories(prev => [...prev, ...toMove]);
    setAssignedCategories(prev => prev.filter(c => !selectedAssigned.has(c.id)));
    setSelectedAssigned(new Set());
  };

  const toggleAvailableSelection = (id: string) => {
    setSelectedAvailable(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAssignedSelection = (id: string) => {
    setSelectedAssigned(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDoubleClickAvailable = (category: AssetCategory) => {
    setAssignedCategories(prev => [...prev, category]);
    setAvailableCategories(prev => prev.filter(c => c.id !== category.id));
    setSelectedAvailable(prev => {
      const next = new Set(prev);
      next.delete(category.id);
      return next;
    });
  };

  const handleDoubleClickAssigned = (category: AssetCategory) => {
    setAvailableCategories(prev => [...prev, category]);
    setAssignedCategories(prev => prev.filter(c => c.id !== category.id));
    setSelectedAssigned(prev => {
      const next = new Set(prev);
      next.delete(category.id);
      return next;
    });
  };

  const saveCategories = async () => {
    if (!selectedRoomType) return;
    setSavingCategories(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/room-types/${selectedRoomType.id}/categories`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ category_ids: assignedCategories.map(c => c.id) }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCategoryDialogOpen(false);
        await loadRoomTypes();
      } else {
        setErrorMessage(data.message || 'Failed to save category mappings');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error saving categories:', error);
      setErrorMessage('An error occurred while saving');
      setErrorDialogOpen(true);
    } finally {
      setSavingCategories(false);
    }
  };

  const canEdit = user?.roles?.some(role => ['admin', 'supervisor'].includes(role.key)) ?? false;
  const canDelete = user?.roles?.some(role => role.key === 'admin') ?? false;

  const columns: ColumnDef<RoomType>[] = [
    createLinkColumn<RoomType>('name', 'Name', (roomType) => openCategoryDialog(roomType)),
    {
      accessorKey: 'key',
      header: 'Key',
      cell: ({ row }) => (
        <code className="text-sm bg-muted px-2 py-1 rounded">{row.getValue('key')}</code>
      ),
    },
    {
      accessorKey: 'categories',
      header: 'Categories',
      cell: ({ row }) => {
        const categories = row.original.categories || [];
        return (
          <div className="flex flex-wrap gap-1 max-w-xs">
            {categories.slice(0, 3).map(cat => (
              <Badge key={cat.id} variant="secondary" className="text-xs">
                {cat.name}
              </Badge>
            ))}
            {categories.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{categories.length - 3} more
              </Badge>
            )}
            {categories.length === 0 && (
              <span className="text-muted-foreground text-sm italic">No categories</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'sort_order',
      header: 'Order',
      cell: ({ row }) => <span className="text-sm">{row.getValue('sort_order')}</span>,
    },
    createBadgeColumn<RoomType>('is_active', 'Status', {
      getBadgeVariant: (value) => {
        const isActive = typeof value === 'boolean' ? value : value === 'true';
        return isActive ? 'default' : 'secondary';
      },
      formatValue: (value) => {
        const isActive = typeof value === 'boolean' ? value : value === 'true';
        return isActive ? 'Active' : 'Inactive';
      },
    }),
    createDateColumn<RoomType>('created_at', 'Created At', 'datetime'),
    {
      id: 'actions',
      header: 'Actions',
      enableHiding: false,
      cell: ({ row }) => {
        const roomType = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openCategoryDialog(roomType)}>
                <FolderOpen className="h-4 w-4 mr-2" />
                Manage Categories
              </DropdownMenuItem>
              {canEdit && (
                <DropdownMenuItem onClick={() => startEdit(roomType)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Room Type
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => handleDeleteClick(roomType)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Room Type
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="room-types" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="room-types" />

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-heading font-bold text-primary leading-tight mb-2">
            Room Types
          </h2>
          <p className="text-muted-foreground">
            Manage room types and their associated product categories for the quoting tool.
          </p>
        </div>

        <div className="mb-2" />

        <DataTable
          columns={columns}
          data={roomTypes}
          loading={loading}
          searchKey="name"
          searchPlaceholder="Search room types..."
          toolbarContentRight={canEdit ? (
            <Button onClick={startCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add Room Type
            </Button>
          ) : undefined}
          emptyMessage="No room types found."
        />

        {/* Create/Edit Room Type Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Room Type' : 'Create Room Type'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Update the room type details.' : 'Add a new room type for quotes.'}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Key</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., living_room" {...field} disabled={!!editing} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Living Room" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Optional description..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="icon"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Icon (optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., sofa" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sort_order"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sort Order</FormLabel>
                        <FormControl>
                          <Input type="number" min={0} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Active</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : editing ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Manage Categories Dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="sm:max-w-[700px]">
            <DialogHeader>
              <DialogTitle>{selectedRoomType?.name}: Manage Categories</DialogTitle>
              <DialogDescription>
                Select which product categories should appear when this room type is selected in quotes.
                Double-click or use arrows to move categories.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-[1fr_auto_1fr] gap-4 py-4">
              {/* Available Categories */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Available Categories</h4>
                <div className="border rounded-md h-64 overflow-y-auto">
                  {availableCategories.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No available categories
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {availableCategories.map(cat => (
                        <div
                          key={cat.id}
                          className={`px-3 py-2 rounded cursor-pointer select-none transition-colors ${
                            selectedAvailable.has(cat.id)
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => toggleAvailableSelection(cat.id)}
                          onDoubleClick={() => handleDoubleClickAvailable(cat)}
                        >
                          {cat.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Arrow Buttons */}
              <div className="flex flex-col items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={moveToAssigned}
                  disabled={selectedAvailable.size === 0}
                  title="Move selected to assigned"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={moveToAvailable}
                  disabled={selectedAssigned.size === 0}
                  title="Move selected to available"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </div>

              {/* Assigned Categories */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Assigned Categories ({assignedCategories.length})</h4>
                <div className="border rounded-md h-64 overflow-y-auto">
                  {assignedCategories.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No categories assigned
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {assignedCategories.map(cat => (
                        <div
                          key={cat.id}
                          className={`px-3 py-2 rounded cursor-pointer select-none transition-colors ${
                            selectedAssigned.has(cat.id)
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => toggleAssignedSelection(cat.id)}
                          onDoubleClick={() => handleDoubleClickAssigned(cat)}
                        >
                          {cat.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="secondary" onClick={() => setCategoryDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveCategories} disabled={savingCategories}>
                {savingCategories ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete Room Type"
          description={`Are you sure you want to delete "${roomTypeToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={deleteRoomType}
          variant="destructive"
        />

        {/* Error Message Dialog */}
        <MessageDialog
          open={errorDialogOpen}
          onOpenChange={setErrorDialogOpen}
          title="Error"
          description={errorMessage}
          variant="destructive"
        />
      </main>
    </div>
  );
}
